import { Injectable } from '@angular/core';
import { IReport, IReportDocument, ReportStatus } from '@shared/interfaces';
import { ApiService } from './api.service';
import dateFormat from 'dateformat';
import { HttpClient } from '@angular/common/http';
import { FileSaverService } from 'ngx-filesaver';
import { IS_TRS_PROJECT } from '../../../current-project';

@Injectable({
    providedIn: 'root'
})
export class FileService {
    constructor(
        private apiService: ApiService,
        private http: HttpClient,
        private fileSaver: FileSaverService
    ) {}

    getUploadLink(id: string, name: string): Promise<{ link: string }> {
        return this.apiService.get(
            `/reports/upload-link?path=${encodeURIComponent(id)}&key=${encodeURIComponent(name)}`
        );
    }

    getUploadLinkForSignature(id: string, type: string, signerId: string): Promise<{ link: string }> {
        return this.apiService.get(
            `/reports/upload-link-single-signature?reportId=${encodeURIComponent(id)}&type=${encodeURIComponent(type)}&signerId=${encodeURIComponent(signerId)}`
        );
    }

    getUploadLinkForReport(id: string): Promise<{ link: string }> {
        return this.apiService.get(`/reports/upload-link-report?path=${encodeURIComponent(id)}`);
    }

    async uploadAttachment(uploadLink: string, body: any): Promise<void> {
        if (IS_TRS_PROJECT) {
            await this.apiService.putExternalWithBearer(uploadLink, body);
        } else {
            await this.apiService.putExternal(uploadLink, body);
        }
    }

    getReportDocument(id: string, key: string): Promise<IReportDocument> {
        return this.apiService.get(`/reports/documents?id=${encodeURIComponent(id)}&path=${encodeURIComponent(key)}`);
    }

    getUploadLinkFaq(id: string, name: string, lang?: string): Promise<{ link: string }> {
        if (lang) {
            return this.apiService.get(
                `/faqs/upload-link?path=${encodeURIComponent(id)}&key=${encodeURIComponent(name)}&lang=${encodeURIComponent(lang)}`
            );
        } else {
            return this.apiService.get(
                `/faqs/upload-link?path=${encodeURIComponent(id)}&key=${encodeURIComponent(name)}`
            );
        }
    }

    async getFaqDocument(id: string, key: string, lang?: string): Promise<void> {
        let dataLink: IReportDocument;
        if (lang) {
            dataLink = await this.apiService.get(
                `/faqs/${encodeURIComponent(id)}/documents?path=${encodeURIComponent(key)}&lang=${lang}`
            );
        } else {
            dataLink = await this.apiService.get(
                `/faqs/${encodeURIComponent(id)}/documents?path=${encodeURIComponent(key)}`
            );
        }

        await this.downloadAttachment(dataLink, key);
    }

    public async downloadAttachment(dataLink: IReportDocument, fileName: string): Promise<void> {
        let document: Blob;
        if (IS_TRS_PROJECT) {
            document = await this.apiService.getExternalWithBearer(dataLink.link, { responseType: 'blob' });
        } else {
            document = await this.http.get(dataLink.link, { responseType: 'blob' }).toPromise();
        }

        this.fileSaver.save(document, fileName);
    }

    async exportReport(report: IReport): Promise<void> {
        const date = new Date(report.modifiedAt);

        const dataLink: IReportDocument = await this.apiService.get(
            `/reports/export?id=${encodeURIComponent(report.id)}`
        );
        const fileName =
            `FY${new Date(report.dueDate).getFullYear()}_Annual_${IS_TRS_PROJECT ? 'TRS' : 'PS'}_Report_${report.responsible.orgCode}_${dateFormat(date, 'yyyy-mm-dd-HH-MM-ss')}` +
            `_${report.reportStatus === ReportStatus.released ? 'final' : 'draft'}_${report.selectedLanguage || 'en'}`.replace(
                new RegExp(' ', 'g'),
                '_'
            );

        await this.downloadAttachment(dataLink, fileName + '.pdf');
    }
}
